/*
 * Winners page (/pages/winners) — sections/winners.liquid.
 *
 * Four behaviours, all client-side over markup Liquid already rendered:
 *
 *   Accordion   one month per <section>, the newest open on load.
 *   Pager       a month with more events than the section's "shown at a time"
 *               setting steps through them with arrows.
 *   Search      filters cards across EVERY month, archived ones included, and
 *               opens whichever months still have a match.
 *   Archives    the sidebar dropdown reveals one archived month in place.
 *
 * The archived months are in the DOM from the start (hidden), which is what lets
 * search reach them without a round trip.
 */
(function () {
  'use strict';

  var ROOT = '[data-cbw]';

  function els(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  /* ---------------------------------------------------------------- months */

  function panelOf(month) {
    return month.querySelector('[data-cbw-panel]');
  }

  function toggleOf(month) {
    return month.querySelector('[data-cbw-toggle]');
  }

  function isOpen(month) {
    var panel = panelOf(month);
    return !!panel && !panel.hidden;
  }

  function setOpen(month, open) {
    var panel = panelOf(month);
    var toggle = toggleOf(month);
    if (!panel || !toggle) return;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) paint(month);
  }

  /* ----------------------------------------------------------------- pager */

  /*
   * Each month keeps its page index on the element itself. Cards hidden by the
   * pager and cards hidden by a search are the same `hidden` attribute, so the
   * pager always works from the cards that survived the current filter — that is
   * what stops "1-3 of 5" appearing over a filtered month showing two cards.
   */
  function visibleCards(month) {
    return els(month, '[data-cbw-card]').filter(function (card) {
      return card.dataset.cbwFiltered !== 'true';
    });
  }

  function paint(month) {
    var root = month.closest(ROOT);
    var perPage = parseInt(root && root.dataset.cbwPerPage, 10) || 3;
    var cards = visibleCards(month);
    var pager = month.querySelector('[data-cbw-pager]');
    var label = month.querySelector('[data-cbw-pager-label]');
    var prev = month.querySelector('[data-cbw-prev]');
    var next = month.querySelector('[data-cbw-next]');

    // A search collapses the pager: the visitor asked for specific winners, so
    // show every one that matched instead of making them page through.
    var searching = root && root.dataset.cbwSearching === 'true';
    var pages = searching ? 1 : Math.max(1, Math.ceil(cards.length / perPage));

    var page = parseInt(month.dataset.cbwPage, 10) || 0;
    if (page > pages - 1) page = pages - 1;
    if (page < 0) page = 0;
    month.dataset.cbwPage = String(page);

    var from = searching ? 0 : page * perPage;
    var to = searching ? cards.length : Math.min(from + perPage, cards.length);

    els(month, '[data-cbw-card]').forEach(function (card) {
      var filtered = card.dataset.cbwFiltered === 'true';
      var idx = cards.indexOf(card);
      card.hidden = filtered || idx < from || idx >= to;
    });

    if (!pager) return;
    if (pages < 2) {
      pager.hidden = true;
      return;
    }
    pager.hidden = false;
    if (label) {
      label.textContent =
        (from + 1) + '–' + to + ' of ' + cards.length + ' event' + (cards.length === 1 ? '' : 's');
    }
    if (prev) prev.disabled = page === 0;
    if (next) next.disabled = page >= pages - 1;
  }

  function step(month, delta) {
    var page = parseInt(month.dataset.cbwPage, 10) || 0;
    month.dataset.cbwPage = String(page + delta);
    paint(month);
  }

  /* ---------------------------------------------------------------- search */

  function runSearch(root, term) {
    var q = (term || '').trim().toLowerCase();
    var searching = q.length > 0;
    root.dataset.cbwSearching = searching ? 'true' : 'false';

    var months = els(root, '[data-cbw-month]');
    var hits = 0;

    months.forEach(function (month) {
      var matched = 0;

      els(month, '[data-cbw-card]').forEach(function (card) {
        var hay = card.dataset.cbwHaystack || '';
        var ok = !searching || hay.indexOf(q) !== -1;
        card.dataset.cbwFiltered = ok ? 'false' : 'true';
        if (ok) matched++;
      });

      hits += matched;

      if (searching) {
        // Reach into the archived months too — the whole point of searching.
        month.hidden = matched === 0;
        if (matched > 0) setOpen(month, true);
      } else {
        // Back to the resting state: archived months hidden again, and only the
        // newest month left open.
        month.hidden = month.classList.contains('cbw-month--archived') && month.dataset.cbwRevealed !== 'true';
        setOpen(month, month.dataset.cbwDefaultOpen === 'true' || month.dataset.cbwRevealed === 'true');
      }

      month.dataset.cbwPage = '0';
      paint(month);
    });

    var empty = root.querySelector('[data-cbw-noresults]');
    if (empty) empty.hidden = !(searching && hits === 0);
  }

  /* -------------------------------------------------------------- archives */

  function fillArchives(root) {
    var widgets = els(root.ownerDocument, '[data-cbw-archive-widget]');
    if (!widgets.length) return;

    var months = els(root, '[data-cbw-month]');
    if (!months.length) return;

    widgets.forEach(function (widget) {
      var select = widget.querySelector('[data-cbw-archive-select]');
      if (!select) return;

      // The sidebar block survives a shopify:section:load that replaces the
      // section, so clear anything a previous pass added before refilling —
      // otherwise every editor save doubles the month list.
      while (select.options.length > 1) select.remove(1);

      months.forEach(function (month) {
        var opt = document.createElement('option');
        opt.value = month.dataset.cbwMonth || '';
        opt.textContent = month.dataset.cbwMonthLabel || opt.value;
        select.appendChild(opt);
      });

      widget.hidden = false;

      if (select.dataset.cbwBound === 'true') return;
      select.dataset.cbwBound = 'true';

      select.addEventListener('change', function () {
        var key = select.value;
        if (!key) return;
        // Resolved now, not closed over: a theme-editor save replaces the
        // section element while this sidebar block stays put.
        var live = document.querySelector(ROOT);
        var month = live && live.querySelector('[data-cbw-month="' + key + '"]');
        if (!month) return;

        month.dataset.cbwRevealed = 'true';
        month.hidden = false;
        setOpen(month, true);
        month.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ------------------------------------------------------------------ init */

  function init(root) {
    if (root.dataset.cbwReady === 'true') return;
    root.dataset.cbwReady = 'true';

    els(root, '[data-cbw-month]').forEach(function (month) {
      month.dataset.cbwPage = '0';
      month.dataset.cbwDefaultOpen = isOpen(month) ? 'true' : 'false';

      var toggle = toggleOf(month);
      if (toggle) {
        toggle.addEventListener('click', function () {
          setOpen(month, !isOpen(month));
        });
      }

      var prev = month.querySelector('[data-cbw-prev]');
      var next = month.querySelector('[data-cbw-next]');
      if (prev) prev.addEventListener('click', function () { step(month, -1); });
      if (next) next.addEventListener('click', function () { step(month, 1); });

      paint(month);
    });

    fillArchives(root);

    // The search widget is a sibling in the sidebar, not inside the section's
    // main column, so it is looked up from the document rather than the root.
    els(root.ownerDocument, '[data-cbw-search-input]').forEach(function (input) {
      var widget = input.closest('[data-cbw-search-widget]');
      var clear = widget && widget.querySelector('[data-cbw-search-clear]');
      var icon = widget && widget.querySelector('[data-cbw-search-icon]');

      var apply = function () {
        // Same reason as the archive select: resolve the section fresh each time
        // so an editor re-render does not leave this filtering a detached tree.
        var live = document.querySelector(ROOT);
        if (live) runSearch(live, input.value);
        if (clear) clear.hidden = input.value.length === 0;
        if (icon) icon.hidden = input.value.length > 0;
      };

      // Re-running init after a section reload must not stack a second listener.
      if (input.dataset.cbwBound === 'true') {
        apply();
        return;
      }
      input.dataset.cbwBound = 'true';

      input.addEventListener('input', apply);
      input.addEventListener('search', apply);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          input.value = '';
          apply();
        }
      });

      if (clear) {
        clear.addEventListener('click', function () {
          input.value = '';
          apply();
          input.focus();
        });
      }
    });
  }

  function boot() {
    els(document, ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when the section is re-rendered or a block is added.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', boot);
})();
